/**
 * Reports Layout Route
 * Tab navigation for report sub-routes. Responsive down to mobile (#960) — the
 * old "Desktop recommended" gate is gone; the report + map are usable on phones
 * (tabs scroll, the table scrolls inside its container, the map panel stacks).
 */

import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { PermissionRouteGuard } from '@/hooks/usePermissions'
import { ViewToggle } from '@/components/common/ViewToggle'
import styles from './reports.module.css'

const REPORT_TABS = [
  { value: 'quarterly', label: 'Quarterly Overview' },
] as const

type ReportTab = typeof REPORT_TABS[number]['value']

export const Route = createFileRoute('/_authenticated/reports')({
  component: ReportsLayout,
})

function ReportsLayout() {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const activeTab = (pathname.split('/').pop() ?? 'quarterly') as ReportTab

  return (
    // Reports are open to anyone with a reports permission (#705). Reps hold
    // reports:view_own and see their own data (report_user_visible + usePracticeScope
    // scope every view); managers/admins hold view_team/view_all for broader data.
    // Users with no reports permission are redirected, so the section can't be
    // opened by URL either.
    <PermissionRouteGuard resource="reports" action="view_own">
      <div className={styles.content}>
        {REPORT_TABS.length > 1 && (
          <ViewToggle
            options={[...REPORT_TABS]}
            value={activeTab}
            onChange={(tab) => navigate({ to: `/reports/${tab}` })}
          />
        )}
        <Outlet />
      </div>
    </PermissionRouteGuard>
  )
}
